import React, { useMemo } from "react";
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useColorModeValue,
  Box,
  Flex,
  Button,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon } from "@chakra-ui/icons";

const FundsTable = ({
  columnsData,
  tableData,
  activeTab,
  setActiveTab,
  onRowClick,
  hideTabs,
}) => {
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  console.log(data, " ... ", tableData);

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return "gray.500";

    switch (normalizedStatus) {
      case "PENDING":
        return "orange.500";
      case "UNDER REVIEW":
        return "purple.500";
      case "APPROVED":
        return "green.500";
      case "REJECTED":
        return "red.500";
      case "FUNDED":
        return "teal.500";
      case "INCOMPLETE":
        return "yellow.500";
      case "DELETED":
        return "gray.500";
      case "CLOSED":
        return "blue.500";
      default:
        return "gray.500";
    }
  };

  const getStatusBgColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return "gray.100";

    switch (normalizedStatus) {
      case "PENDING":
        return "orange.100";
      case "UNDER REVIEW":
        return "purple.100";
      case "APPROVED":
        return "green.100";
      case "REJECTED":
        return "red.100";
      case "FUNDED":
        return "teal.100";
      case "INCOMPLETE":
        return "yellow.100";
      case "DELETED":
        return "gray.100";
      case "CLOSED":
        return "blue.100";
      default:
        return "gray.100";
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'Approved';
      case 'UNDER REVIEW':
        return 'Under Review';
      case 'REJECTED':
        return 'Rejected';
      case 'FUNDED':
        return 'Funded';
      case 'INCOMPLETE':
        return 'Incomplete';
      case 'CLOSED':
        return 'Closed';
      case 'DELETED':
        return 'Deleted';
      case 'PENDING':
        return 'Pending';
      case 'DRAFT':
        return 'Draft';
      default:
        return status || 'N/A';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Check if the date is in DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${day}/${month}/${year}`;
    }
    
    // Handle standard date format
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // const tabOptions = ["All", "Partial", "Settled"];
  const tabOptions = [];

  return (
    <Box>
      {!hideTabs && (
        <Flex mb={4}>
          {tabOptions.map((tab) => (
            <Button
              key={tab}
              variant="ghost"
              bg={activeTab === tab ? "purple.50" : "transparent"}
              color={activeTab === tab ? "purple.500" : "gray.500"}
              onClick={() => setActiveTab(tab)}
              mr={2}>
              {tab}
            </Button>
          ))}
        </Flex>
      )}
      <Box>
        <Table variant="simple">
          <Thead position="sticky" top="0" bg="white">
            <Tr>
              {columns.map((column, index) => (
                <Th key={index} pe="10px" borderColor={borderColor}>
                  <Text color="gray.600" fontSize="12px" fontWeight="700">
                    {column.Header}
                  </Text>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {data.map((row, rowIndex) => (
              <Tr
                key={rowIndex}
                cursor={onRowClick ? "pointer" : "default"}
                _hover={onRowClick ? { bg: "gray.50" } : {}}
                onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {columns.map((column, colIndex) => {
                  let cellValue = row[column.accessor];
                  let cellContent = cellValue;

                  if (column.Header === "Status") {
                    cellContent = (
                      <Text
                        color={getStatusColor(cellValue)}
                        fontSize="sm"
                        fontWeight="500"
                        borderRadius="8px"
                        px={3}
                        py={1}
                        bg={getStatusBgColor(cellValue)}
                        display="inline-block">
                        {getStatusDisplayName(cellValue)}
                      </Text>
                    );
                  } else if (column.Header === "Actions" && column.Cell) {
                    // Use custom Cell renderer if provided in the column definition
                    cellContent = column.Cell({ row });
                  } else if (column.Header === "Actions" && !column.Cell) {
                    // Default action button if no custom Cell renderer
                    cellContent = (
                      <IconButton
                        icon={<ViewIcon color="purple.400" />}
                        variant="ghost"
                        size="sm"
                        aria-label="View"
                        _hover={{ bg: "purple.50" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRowClick) onRowClick(row);
                        }}
                      />
                    );
                  } else if (
                    column.Header === "System ID" ||
                    column.Header === "Invoice Number" ||
                    column.Header === "Company Name"
                  ) {
                    cellContent = (
                      <Text
                        as="span"
                        color="purple.500"
                        textDecoration="underline"
                        fontWeight="600"
                        cursor="pointer"
                        _hover={{
                          textDecoration: "underline",
                          color: "purple.600",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRowClick) onRowClick(row);
                        }}>
                        {cellValue}
                      </Text>
                    );
                  } else if (
                    column.Header === "Funded Amount" ||
                    column.Header === "Remaining Balance" ||
                    column.Header === "Amount" ||
                    column.Header === "Collected Amount" ||
                    column.Header === "Remaining Amount"
                  ) {
                    // Handle both string and number formats
                    const numericValue = typeof cellValue === 'string' 
                      ? parseFloat(cellValue.replace(/[^0-9.-]+/g, ''))
                      : Number(cellValue);
                    cellContent = formatCurrency(numericValue);
                  } else if (column.Header === "Date" || column.accessor === "date") {
                    cellContent = (
                      <Text>
                        {formatDate(cellValue)}
                      </Text>
                    );
                  } else if (column.Header === "Type" || column.accessor === "type") {
                    cellContent = (
                      <Text
                        color={getStatusColor(cellValue)}
                        fontSize="sm"
                        fontWeight="500"
                        borderRadius="8px"
                        px={3}
                        py={1}
                        bg={getStatusBgColor(cellValue)}
                        display="inline-block">
                        {cellValue}
                      </Text>
                    );
                  } else if (column.Header === "Notes" || column.accessor === "notes") {
                    cellContent = (
                      <Text
                        color={textColor}
                        fontSize="sm"
                        fontWeight="500"
                        maxW="250px"
                        whiteSpace="pre-line"
                        wordBreak="break-word"
                      >
                        {cellValue || '-'}
                      </Text>
                    );
                  }

                  return (
                    <Td
                      key={colIndex}
                      fontSize={{ sm: "14px" }}
                      minW={{ sm: "120px", md: "150px", lg: "auto" }}
                      borderColor={borderColor}>
                      {cellContent || (cellValue === 0 ? formatCurrency(0) : 'N/A')}
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default FundsTable;
